import { Window } from '@execonline-inc/browser-window.private';
import { observer } from 'mobx-react';
import { emptyFragment } from '@execonline-inc/execonline-ui';
import { NotTranslated, T } from '../../../../../../Translations';
import { useOpenable } from '../../../../../Modal/UseOpenable';
import { ProductLicenseOverview } from '../../../Types';
import LicenseUsageDetailsModal from './LicenseUsageDetailsModal';
import OverallocatedAlert from './OverallocatedAlert';
import LicenseStatusStartOnDisplay from './LicenseStatusStartOnDisplay';
import LicenseStatusEndOnDisplay from './LicenseStatusEndOnDisplay';
import { currentUserStore } from '../../../../../../CurrentUserStore';
import { findLink } from '../../../../../../LinkyLinky';

interface Props {
  index: number;
  license: ProductLicenseOverview;
}

function LicenseUsageOverview({ index, license }: Props) {
  const { modalState, open, close } = useOpenable();
  const targetId = `products-usage-overview-license-${index}-label`;

  const labelFor = (license: ProductLicenseOverview) => {
    return license.publicName
      .map((publicName) => (
        <span
          className="font-light text-sm bg-[#187a90] text-white px-2 py-1 mx-2 rounded-sm"
          data-intercom-target={targetId}
        >
          <NotTranslated text={publicName} />
        </span>
      ))
      .getOrElse(emptyFragment);
  };

  const downloadCsv = () => {
    findLink('product-license-export', currentUserStore.links)
      .map((link) => link.href.replace('{product_license_id}', license.id.toString()))
      .map((url) => {
        Window.openWindow(url, '_blank');
      });
  };

  return (
    <>
      <tr>
        <td className="font-semibold pt-8">
          <NotTranslated text={license.name} />
          {labelFor(license)}
        </td>
      </tr>
      <tr>
        <td className="py-2">
          <LicenseStatusStartOnDisplay license={license} />
        </td>
      </tr>
      <tr>
        <td className="align-top">{<LicenseStatusEndOnDisplay license={license} />}</td>
        <td className="pl-2 align-top" align="right" data-test-activated={license.id}>
          <T
            kind="{{usedCount}} of {{totalCount}}"
            usedCount={license.activatedCount}
            totalCount={license.licenseQuantity}
          />
        </td>
        <td className="pl-4 align-top" align="right" data-test-allocated={license.id}>
          <div className="flex items-center justify-end gap-1.5">
            <OverallocatedAlert license={license} />
            {license.allocatedCount}
          </div>
        </td>
        <td className="pl-8 align-top">
          <div className="flex items-center gap-2">
            <button onClick={open} className="text-teal-600">
              <T kind="View" />
            </button>
            <span>|</span>
            <button onClick={downloadCsv} className="text-teal-600">
              <T kind="Download" />
            </button>
          </div>
          <LicenseUsageDetailsModal license={license} modalState={modalState} close={close} />
        </td>
      </tr>
    </>
  );
}

export default observer(LicenseUsageOverview);
